import type { DrawCustomMode, DrawCustomModeThis, MapboxDrawOptions, MapMouseEvent } from "@mapbox/mapbox-gl-draw";
import type { LineString, MultiLineString, MultiPolygon, Point as PointGeometry, Polygon, Position } from "geojson";
import { Feature } from "geojson";
import { LngLat, Map as MapboxMap } from "mapbox-gl";

import {
  cleanCoords,
  distance,
  featureCollection,
  getCoords,
  lineString as turfLineString,
  midpoint,
  nearestPoint,
  nearestPointOnLine,
  point as turfPoint,
  polygonToLine,
} from "fond/turf";

import { setPreviewFeatures } from "./snapPreview";

/**
 * Extended state interface for snap modes that tracks snapping-related state
 */
interface SnapModeState {
  options: MapboxDrawOptions;
  map: MapboxMap;
  snappedLng?: number;
  snappedLat?: number;
  prevSnappedLng?: number;
  prevSnappedLat?: number;
  wasSnapping?: boolean;
  showVerticalSnapLine?: boolean;
  showHorizontalSnapLine?: boolean;
  snapList?: Feature[];
}

/**
 * Finds the closest point and closest line/polygon separately.
 * Returns both so the caller can decide which to use based on snap distance.
 *
 * @param lngLat - The position to snap from
 * @param features - The features to consider for snapping
 * @returns Object with closestPoint, closestLine, and closestOverall
 */
function calcClosestFeatures(lngLat: { lng: number; lat: number }, features: Feature[]): any {
  const calcFeatureDistances = (lngLatParam: { lng: number; lat: number }, feature: Feature) => {
    const P = [lngLatParam.lng, lngLatParam.lat];
    const isMarker = feature.geometry.type === "Point";
    const isPolygon = feature.geometry.type === "Polygon";
    const isMultiPolygon = feature.geometry.type === "MultiPolygon";
    const isMultiPoint = feature.geometry.type === "MultiPoint";

    const latlngs = getCoords(feature as Feature<PointGeometry | MultiPolygon | Polygon | LineString | MultiLineString>);

    if (isMarker) {
      const [lng, lat] = latlngs as [number, number];
      return {
        latlng: { lng, lat },
        distance: distance(latlngs as Position, P as Position),
        isMarker: true,
      };
    }

    if (isMultiPoint) {
      const np = nearestPoint(P, featureCollection((latlngs as number[][]).map((x: number[]) => turfPoint(x))));
      const c = np.geometry.coordinates;
      return {
        latlng: { lng: c[0], lat: c[1] },
        distance: np.properties.distanceToPoint,
        isMarker: true,
      };
    }

    let lines: Feature<LineString | MultiLineString> | ReturnType<typeof featureCollection> =
      isPolygon || isMultiPolygon ? polygonToLine(feature as Feature<Polygon | MultiPolygon>) : (feature as Feature<LineString | MultiLineString>);
    let nearestPt: Feature<PointGeometry>;

    // Clean LineString/MultiLineString coordinates to remove duplicate consecutive points
    if (!isPolygon && !isMultiPolygon) {
      lines = cleanCoords(lines) as Feature<LineString | MultiLineString>;
    }

    if (isPolygon) {
      const lineStrings =
        lines.geometry.type === "LineString"
          ? [turfLineString(lines.geometry.coordinates)]
          : lines.geometry.coordinates.map((coords: number[][]) => turfLineString(coords));

      const nearestPointsOfEachFeature = lineStrings.map((feat: any) => ({
        feature: feat,
        point: nearestPointOnLine(feat, P),
      }));
      nearestPointsOfEachFeature.sort((a: any, b: any) => a.point.properties.dist - b.point.properties.dist);

      lines = nearestPointsOfEachFeature[0].feature;
      nearestPt = nearestPointsOfEachFeature[0].point;
    } else if (isMultiPolygon) {
      const lineStrings = lines.features
        .flatMap((feat: any) => {
          if (feat.geometry.type === "LineString") {
            return [feat.geometry.coordinates];
          } else {
            return feat.geometry.coordinates;
          }
        })
        .map((coords: number[][]) => turfLineString(coords));

      const nearestPointsOfEachFeature = lineStrings.map((feat: any) => ({
        feature: feat,
        point: nearestPointOnLine(feat, P),
      }));
      nearestPointsOfEachFeature.sort((a: any, b: any) => a.point.properties.dist - b.point.properties.dist);

      lines = nearestPointsOfEachFeature[0].feature;
      nearestPt = nearestPointsOfEachFeature[0].point;
    } else {
      nearestPt = nearestPointOnLine(lines as Feature<LineString | MultiLineString>, P);
    }

    const [lng, lat] = nearestPt.geometry.coordinates;
    let segmentIndex = nearestPt.properties?.index ?? 0;
    let { coordinates } = lines.geometry;

    if (lines.geometry.type === "MultiLineString") {
      coordinates = lines.geometry.coordinates[nearestPt.properties?.multiFeatureIndex ?? 0];
    }

    if (segmentIndex + 1 === coordinates.length) {
      segmentIndex -= 1;
    }

    return {
      latlng: { lng, lat },
      segment: coordinates.slice(segmentIndex, segmentIndex + 2),
      distance: nearestPt.properties?.dist ?? 0,
      isMarker: false,
    };
  };

  let closestFeature: any = {};
  let closestPoint: any = null;
  let closestLine: any = null;

  // Find closest point and closest line separately
  features.forEach((feature) => {
    const results = calcFeatureDistances(lngLat, feature);

    // Skip invalid features
    if (!results) {
      return;
    }

    if (results.isMarker) {
      // Track closest point
      if (!closestPoint || results.distance < closestPoint.distance) {
        closestPoint = { ...results, feature };
      }
    } else if (!closestLine || results.distance < closestLine.distance) {
      // Track closest line/polygon
      closestLine = { ...results, feature };
    }

    // Track overall closest
    if (closestFeature.distance === undefined || results.distance < closestFeature.distance) {
      closestFeature = { ...results, feature };
    }
  });

  // Return both options so caller can decide based on snap distance
  return {
    closestPoint,
    closestLine,
    closestOverall: closestFeature,
  };
}

/**
 * Custom snap function that prioritizes Point features over LineStrings/Polygons
 * when BOTH are within snap distance.
 *
 * Excludes colocated features from snapping to prevent snapping to features that are moving.
 */
export function snapWithPointPriority(state: SnapModeState, e: MapMouseEvent): { lng: number; lat: number } {
  let { lng } = e.lngLat;
  let { lat } = e.lngLat;

  // Holding alt bypasses all snapping
  if (e.originalEvent.altKey) {
    state.showVerticalSnapLine = false;
    state.showHorizontalSnapLine = false;
    return { lng, lat };
  }

  if (!state.snapList || state.snapList.length <= 0) {
    return { lng, lat };
  }

  if (!state.options.snap) {
    return { lng, lat };
  }

  // Filter out colocated features from the snapList to prevent snapping to features that are moving
  let filteredSnapList = state.snapList;
  if (state.colocatedFeatures && state.colocatedFeatures.length > 0) {
    const colocatedIds = new Set<string | number>(state.colocatedFeatures.map((c: any) => c.id));
    filteredSnapList = state.snapList.filter((f: any) => f.id && !colocatedIds.has(f.id));
  }

  if (filteredSnapList.length === 0) {
    return { lng, lat };
  }

  // Get closest point and line separately
  const { closestPoint, closestLine } = calcClosestFeatures({ lng, lat }, filteredSnapList);

  if (!closestPoint && !closestLine) {
    return { lng, lat };
  }

  // Calculate snap distance threshold in meters
  const metersPerPixel = (latitude: number, zoomLevel: number) => {
    const earthCircumference = 40075017;
    const latitudeRadians = latitude * (Math.PI / 180);
    return (earthCircumference * Math.cos(latitudeRadians)) / 2 ** (zoomLevel + 8);
  };

  const snapPx = state.options.snapOptions?.snapPx ?? 15;
  const snapPointPx = state.options.snapOptions?.snapPointPx ?? snapPx; // Use snapPointPx if provided, otherwise fall back to snapPx

  const mpp = metersPerPixel(lat, state.map.getZoom());
  const lineSnapDistanceMeters = snapPx * mpp;
  const pointSnapDistanceMeters = snapPointPx * mpp;

  // Check which features are within snap distance (convert km to meters)
  const pointInRange = closestPoint && closestPoint.distance * 1000 < pointSnapDistanceMeters;
  const lineInRange = closestLine && closestLine.distance * 1000 < lineSnapDistanceMeters;

  // Decide which feature to use: prefer point if both are in range
  let closestLayer: any;
  if (pointInRange && lineInRange) {
    closestLayer = closestPoint; // Both snappable, prefer point
  } else if (pointInRange) {
    closestLayer = closestPoint; // Only point is snappable
  } else if (lineInRange) {
    closestLayer = closestLine; // Only line is snappable
  } else {
    return { lng, lat }; // Neither is snappable
  }

  // Calculate snap position for chosen feature
  let snapLatLng: { lng: number; lat: number };
  const { isMarker } = closestLayer;
  const snapVertexPriorityDistance = state.options.snapOptions?.snapVertexPriorityDistance ?? 1.25;

  if (isMarker) {
    // Point feature - snap directly to it
    snapLatLng = closestLayer.latlng;
  } else {
    // Line/Polygon feature - check if we should snap to vertex or edge
    const A = closestLayer.segment[0];
    const B = closestLayer.segment[1];
    const C = [closestLayer.latlng.lng, closestLayer.latlng.lat];

    const distanceAC = distance(A, C);
    const distanceBC = distance(B, C);

    let closestVertexLatLng = distanceAC < distanceBC ? A : B;
    let shortestDistance = distanceAC < distanceBC ? distanceAC : distanceBC;

    // Check midpoint if enabled
    if (state.options.snapOptions?.snapToMidPoints) {
      const M = midpoint(A, B).geometry.coordinates;
      const distanceMC = distance(M, C);

      if (distanceMC < distanceAC && distanceMC < distanceBC) {
        closestVertexLatLng = M;
        shortestDistance = distanceMC;
      }
    }

    // Snap to vertex if close enough, otherwise snap to edge
    if (shortestDistance < snapVertexPriorityDistance) {
      const [lngSnap, latSnap] = closestVertexLatLng;
      snapLatLng = { lng: lngSnap, lat: latSnap };
    } else {
      snapLatLng = { lng: C[0], lat: C[1] };
    }
  }

  return snapLatLng;
}

/**
 * Shared logic for handling mouse move events with snap preview functionality.
 * This is used by both CustomSnapLineMode and CustomSnapPointMode to avoid code duplication.
 *
 * @param context - The draw mode context (this)
 * @param baseMode - The base snap mode (SnapLineMode or SnapPointMode) to delegate to
 * @param state - The current mode state
 * @param e - The mouse move event
 * @param previewSourceId - The ID of the preview source for this mode
 * @returns The result from the base mode's onMouseMove handler
 */
export function handleSnapMouseMove(
  context: DrawCustomModeThis,
  baseMode: DrawCustomMode,
  state: SnapModeState,
  e: MapMouseEvent,
  previewSourceId: string
): void {
  // Store the original mouse coordinates before snapping
  const originalLng = e.lngLat.lng;
  const originalLat = e.lngLat.lat;

  // Use our custom snap function that prioritizes Points over LineStrings
  const { lng, lat } = snapWithPointPriority(state, e);

  // Store snapped coordinates in state for onClick handler
  state.snappedLng = lng;
  state.snappedLat = lat;

  // Update the event with snapped coordinates
  e.lngLat = new LngLat(lng, lat);

  // Call the base onMouseMove with the snapped coordinates
  const result = baseMode.onMouseMove?.call(context, state, e);

  // Check if coordinates actually changed due to snapping
  const threshold = 0.000001; // Small threshold for floating point comparison
  const didSnap = Math.abs(lng - originalLng) > threshold || Math.abs(lat - originalLat) > threshold;

  // Track if we're at the same snap point as before
  // This handles the case where the cursor is already at a snap point
  const sameAsPrevious =
    state.prevSnappedLng !== undefined &&
    state.prevSnappedLat !== undefined &&
    Math.abs(lng - state.prevSnappedLng) <= threshold &&
    Math.abs(lat - state.prevSnappedLat) <= threshold;

  // If we snapped before and we're still at the same position, keep showing the preview
  const shouldShowPreview = didSnap || (sameAsPrevious && state.wasSnapping);

  // Track snapping state for next frame
  state.prevSnappedLng = lng;
  state.prevSnappedLat = lat;
  state.wasSnapping = didSnap || (sameAsPrevious && state.wasSnapping);

  // Show snap preview if we're snapping or at a snap point from before
  if (state.options?.snap && shouldShowPreview) {
    // Show the snap preview circle at the snapped location
    setPreviewFeatures(context.map, previewSourceId, [turfPoint([lng, lat])]);
  } else {
    // Hide the preview if not snapping
    setPreviewFeatures(context.map, previewSourceId, []);
  }

  return result;
}
